import { useEffect, useState } from "react";
import type { ScannedBoard } from "../workspace/workspace_scan";
import type { WorkspaceGateway } from "../workspace/workspace_gateway";

export interface BoardPreviewProps {
  readonly board: ScannedBoard;
  readonly gateway: WorkspaceGateway;
}

/** Loads one replaceable local preview and releases its object URL on change. */
export function BoardPreview({ board, gateway }: BoardPreviewProps) {
  const [source, setSource] = useState<string | null>(null);

  useEffect(() => {
    if (!board.previewAvailable) {
      setSource(null);
      return;
    }
    let active = true;
    let objectUrl: string | null = null;

    /** Reads only the generated preview, never an original board asset. */
    async function loadPreview(): Promise<void> {
      try {
        const bytes = await gateway.readFile([...board.path, "preview.webp"]);
        objectUrl = URL.createObjectURL(new Blob([bytes], { type: "image/webp" }));
        if (active) {
          setSource(objectUrl);
        } else {
          URL.revokeObjectURL(objectUrl);
          objectUrl = null;
        }
      } catch {
        if (active) {
          setSource(null);
        }
      }
    }

    void loadPreview();
    return () => {
      active = false;
      if (objectUrl !== null) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [board.path, board.previewAvailable, gateway]);

  return source === null ? (
    <span className="board-preview__empty" aria-hidden="true">
      S
    </span>
  ) : (
    <img className="board-preview__image" src={source} alt="" />
  );
}
